/**
 * useTickStream — single shared WebSocket to /ws/ticks.
 *
 * Multiple ChartPane components subscribe; this hook deduplicates upstream
 * subscriptions and routes ticks to all subscribers.
 *
 * Reconnects with exponential backoff. Resubscribes on reconnect.
 *
 * Usage:
 *   const { lastTick, status } = useTickStream(symbol, exchange);
 *   // lastTick = { last_price, ts } | null
 *   // status   = 'connecting' | 'connected' | 'disconnected'
 */

import { useState, useEffect, useRef } from "react";
import { useAuthStore } from "@/store/auth";

interface Tick {
  last_price: number;
  ts: string;
}

type Status = "connecting" | "connected" | "disconnected";

interface PerSymbolListener {
  symbol: string;
  exchange: string;
  cb: (tick: Tick) => void;
}

// ─── Module-singleton WS state ─────────────────────────────────────────────
let ws: WebSocket | null = null;
let status: Status = "disconnected";
let statusListeners = new Set<(s: Status) => void>();
let listeners = new Map<string, Set<PerSymbolListener>>(); // key = `${exchange}:${symbol}`
let reconnectTimer: number | null = null;
let reconnectDelay = 1000;
// readyState === OPEN only means the handshake finished. The backend closes
// the socket unless the FIRST message is auth, and does not read subscribes
// until auth_ok has been sent -- so a subscribe fired between open and
// auth_ok is lost, silently, and no tick ever arrives. Gate on this instead.
let authed = false;

function keyOf(symbol: string, exchange: string) {
  return `${exchange}:${symbol}`;
}

function setStatus(s: Status) {
  status = s;
  statusListeners.forEach((fn) => fn(s));
}

function getWsUrl(): string {
  // Same origin as the page, switch http(s) → ws(s)
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/ws/ticks`;
}

function openSocket(jwt: string) {
  if (ws && ws.readyState <= 1) return; // already connecting/open

  setStatus("connecting");
  const sock = new WebSocket(getWsUrl());

  sock.onopen = () => {
    authed = false;
    sock.send(JSON.stringify({ type: "auth", token: jwt }));
  };

  sock.onmessage = (e) => {
    let msg: any;
    try { msg = JSON.parse(e.data); } catch { return; }

    if (msg.type === "auth_ok") {
      authed = true;
      setStatus("connected");
      reconnectDelay = 1000;
      // Re-send subscriptions for everything we have listeners for
      const symbols = Array.from(listeners.keys()).map((k) => {
        const [exchange, ...rest] = k.split(":");
        return { symbol: rest.join(":"), exchange };
      });
      if (symbols.length > 0) {
        sock.send(JSON.stringify({ type: "subscribe", symbols }));
      }
      return;
    }

    if (msg.type === "tick") {
      const k = keyOf(msg.symbol, msg.exchange);
      const subs = listeners.get(k);
      if (subs) {
        const tick: Tick = { last_price: msg.last_price, ts: msg.ts };
        subs.forEach((l) => l.cb(tick));
      }
    }
  };

  sock.onclose = () => {
    ws = null;
    // Without this a dropped socket leaves authed=true, and the next
    // subscribe is sent into a dead connection instead of waiting for the
    // reconnect's auth_ok to resubscribe.
    authed = false;
    setStatus("disconnected");
    // Reconnect with backoff (capped at 30s)
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    const delay = reconnectDelay;
    reconnectDelay = Math.min(reconnectDelay * 2, 30000);
    reconnectTimer = window.setTimeout(() => {
      const jwt = useAuthStore.getState().accessToken;
      if (jwt) openSocket(jwt);
    }, delay);
  };

  sock.onerror = () => {
    // onclose will fire next; cleanup happens there
  };

  ws = sock;
}

function ensureOpen() {
  const jwt = useAuthStore.getState().accessToken;
  if (!jwt) return;
  if (ws === null) openSocket(jwt);
}

function subscribeUpstream(symbol: string, exchange: string) {
  // Not authed yet: the listener is already registered, and the auth_ok
  // handler resubscribes everything in `listeners`, so this is a no-op
  // rather than a loss.
  if (ws && ws.readyState === 1 && authed) {
    ws.send(JSON.stringify({ type: "subscribe", symbols: [{ symbol, exchange }] }));
  }
}

function unsubscribeUpstream(symbol: string, exchange: string) {
  if (ws && ws.readyState === 1 && authed) {
    ws.send(JSON.stringify({ type: "unsubscribe", symbols: [{ symbol, exchange }] }));
  }
}

// ─── Public hook ────────────────────────────────────────────────────────────
export function useTickStream(symbol: string, exchange: string) {
  const [lastTick, setLastTick] = useState<Tick | null>(null);
  const [connStatus, setConnStatus] = useState<Status>(status);
  const idRef = useRef<PerSymbolListener | null>(null);

  useEffect(() => {
    ensureOpen();

    // Track status changes globally
    const sl = (s: Status) => setConnStatus(s);
    statusListeners.add(sl);

    // Register this listener
    const k = keyOf(symbol, exchange);
    const listener: PerSymbolListener = { symbol, exchange, cb: setLastTick };
    idRef.current = listener;

    const wasEmpty = !listeners.has(k) || listeners.get(k)!.size === 0;
    if (!listeners.has(k)) listeners.set(k, new Set());
    listeners.get(k)!.add(listener);
    if (wasEmpty) subscribeUpstream(symbol, exchange);

    return () => {
      statusListeners.delete(sl);
      const set = listeners.get(k);
      if (set) {
        set.delete(listener);
        if (set.size === 0) {
          listeners.delete(k);
          unsubscribeUpstream(symbol, exchange);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange]);

  return { lastTick, status: connStatus };
}
